import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

const glossarySections = [
  {
    title: 'Grades de conviction',
    description: 'Le grade condense la qualite globale du setup (structure, confirmation, risque) sur une echelle lisible.',
    entries: [
      {
        term: 'A+ (Institutionnel)',
        meaning: 'Setup tres propre avec confirmations multiples et execution generalement robuste.',
      },
      {
        term: 'A (Solide)',
        meaning: 'Configuration forte avec bon contexte et risque/retour coherent.',
      },
      {
        term: 'B (Modere)',
        meaning: 'Signal exploitable mais avec un ou plusieurs points de vigilance.',
      },
      {
        term: 'C (Fragile)',
        meaning: 'Structure faible ou ambiguë, a traiter de facon plus defensive.',
      },
    ],
  },
  {
    title: 'Termes de structure',
    description: 'Elements techniques utilises pour qualifier la figure et son point d entree.',
    entries: [
      {
        term: 'Breakout',
        meaning: 'Niveau de prix a partir duquel la figure est validee (cassure de la zone cle).',
      },
      {
        term: 'Target (objectif)',
        meaning: 'Zone theorique de sortie en gain si le scenario se deroule comme prevu.',
      },
      {
        term: 'Stop / invalidation',
        meaning: 'Niveau qui invalide le setup. Il borne le risque de la position.',
      },
      {
        term: 'R/R (Risk/Reward)',
        meaning: 'Ratio gain potentiel / perte potentielle. Exemple: R/R 2.0 signifie ~2 unites de gain visees pour 1 unite de risque. En general, plus il est eleve, plus le profil est favorable.',
      },
    ],
  },
  {
    title: 'Termes de confirmation',
    description: 'Signaux complementaires utilises par le scanner pour filtrer les faux positifs.',
    entries: [
      {
        term: 'Confiance',
        meaning: 'Estimation de fiabilite du signal detecte sur la fenetre analysee.',
      },
      {
        term: 'Volume confirmation',
        meaning: 'Validation du mouvement par une dynamique de volume coherente avec la cassure.',
      },
      {
        term: 'Fakeout risk',
        meaning: 'Probabilite de faux breakout suivi d un retour sous le niveau de cassure.',
      },
      {
        term: 'Signal fusion state',
        meaning: 'Etat de convergence des differents signaux internes utilises par le moteur.',
      },
    ],
  },
];

function GlossaryTermCard({ term, meaning }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(16,37,43,0.1)',
        background: '#fff',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c', mb: 0.5 }}>
        {term}
      </Typography>
      <Typography variant="body2" sx={{ color: '#4f6168', lineHeight: 1.75 }}>
        {meaning}
      </Typography>
    </Paper>
  );
}

const PatternGlossaryDrawer = ({ open, onClose }) => {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', md: 760 }, maxWidth: '100vw', height: '100%', background: '#f7f3ea' }}>
        <Stack spacing={2.2} sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box>
              <Typography variant="overline" sx={{ color: '#6b7d86', letterSpacing: '0.12em' }}>
                Lexique du pattern scanner
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#19323c' }}>
                Comprendre les termes utilises
              </Typography>
              <Typography variant="body2" sx={{ color: '#52636c', maxWidth: 560, lineHeight: 1.7 }}>
                Definitions courtes pour lire rapidement les verdicts, grades et indicateurs de decision du scanner.
              </Typography>
            </Box>
            <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 99 }}>
              Fermer
            </Button>
          </Stack>

          <Chip
            label="Aide contextuelle: grades, breakout, risque et confirmations"
            sx={{ alignSelf: 'flex-start', bgcolor: '#efe7da', color: '#19323c', fontWeight: 700 }}
          />

          <Divider />

          <Stack spacing={2.2} sx={{ pb: 2 }}>
            {glossarySections.map((section) => (
              <Paper
                key={section.title}
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 4,
                  border: '1px solid rgba(16,37,43,0.08)',
                  background: 'linear-gradient(180deg, #fffdf8 0%, #f6efe2 100%)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#19323c' }}>
                  {section.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, mb: 1.4, color: '#586a72', lineHeight: 1.7 }}>
                  {section.description}
                </Typography>
                <Stack spacing={1}>
                  {section.entries.map((entry) => (
                    <GlossaryTermCard key={entry.term} term={entry.term} meaning={entry.meaning} />
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default PatternGlossaryDrawer;
